(ns arthur.domain.outline
  "A brush stroke, as pixels and then as polygons.

  Flash's brush: what you paint becomes filled shapes the moment you let go, and
  the stroke is not kept. Here the stroke is first a MASK — a byte per stage
  pixel, stamped with the brush's disc as the pointer moves, so what is on screen
  while painting is exactly the pixels — and on letting go each piece of it is
  traced round its pixel edges and simplified to as many points as are asked for,
  as the mouth's ring is.

  The trace walks pixel CORNERS, so before simplifying, a traced ring filled by
  `raster/fill-poly-buf!` is the mask again exactly.

  A HOLE STAYS A HOLE, and a shape is still one ring. A loop painted round a gap
  is a piece with a hole in it; the hole is traced as well, and joined to the
  outside by a HORIZONTAL bridge along a whole-pixel row — out and back along
  the same line. The fill samples at pixel centres (y + 0.5) and an edge with no
  height crosses no scanline, so the bridge draws nothing, the even-odd fill
  leaves the hole empty, and nothing downstream has to know a ring can have
  one. It is how earcut and the TrueType rasterisers take holes too.

  `pieces` keeps the trace — outside and holes, at full resolution — so the
  fit can change after the stroke, and `polygon` is the one place it is fitted
  and bridged: the preview and the saved shape are the same function's output,
  so what is on screen while painting is what is kept.

  FITTED TO A TOLERANCE, not cut to a count: every point of the traced edge is
  within so many pixels of the polygon (Douglas-Peucker), so points go where
  the shape bends and none are spent on a straight run."
  (:require ["polygon-clipping" :as clipping]
            [arthur.domain.raster :as raster]))

(defn mask [w h] {:w w :h h :buf (js/Uint8Array. (* w h))})

(defn stamp!
  "The brush, `size` pixels across, from `[ax ay]` to `[bx by]`: a disc every
  half radius along the way, so a fast stroke is still one stroke."
  [m [ax ay] [bx by] size]
  (let [r     (max 0.5 (/ size 2))
        steps (max 1 (js/Math.ceil (/ (js/Math.hypot (- bx ax) (- by ay)) (max 0.5 (/ r 2)))))]
    (dotimes [i (inc steps)]
      (let [t (/ i steps)]
        (raster/fill-disc! m (+ ax (* t (- bx ax))) (+ ay (* t (- by ay))) r 1))))
  m)

(defn- flood!
  "Label the 4-connected region of pixels whose ink is `ink` from pixel `start`
  with `id` in `lab`, inside the box `[x0 y0 x1 y1]`. Its size, and whether it
  reaches the box's edge — a gap that does is outside, not a hole.

  A typed stack and no allocation per pixel: this runs on every pointer move."
  [^js lab ^js buf w [x0 y0 x1 y1] ink start id ^js stack]
  (aset lab start id)
  (aset stack 0 start)
  (let [top (volatile! 1) size (volatile! 0) edge? (volatile! false)
        visit! (fn [q] (when (and (zero? (aget lab q)) (== ink (aget buf q)))
                         (aset lab q id)
                         (aset stack @top q)
                         (vswap! top inc)))]
    (while (pos? @top)
      (let [p (aget stack (vswap! top dec))
            x (mod p w) y (quot p w)]
        (vswap! size inc)
        (when (or (== x x0) (== x x1) (== y y0) (== y y1)) (vreset! edge? true))
        (when (< x0 x) (visit! (dec p)))
        (when (< x x1) (visit! (inc p)))
        (when (< y0 y) (visit! (- p w)))
        (when (< y y1) (visit! (+ p w)))))
    {:size @size :edge? @edge?}))

(def ^:private dirs [[1 0] [0 1] [-1 0] [0 -1]])

(defn- ring
  "The edge of the region `in?` that starts at pixel `start`, the first of it in
  raster order, as flat corner points: walked with the region on the right and a
  point only where the walk turns. Round a piece, that is its outside; round a
  hole, the inside edge of the piece around it."
  [w in? start]
  (let [x0 (mod start w) y0 (quot start w)]
    ;; Heading east along the start pixel's top edge, the region is below: on the
    ;; right. At each corner the two pixels ahead decide: the one ahead on the
    ;; right empty turns right, the one ahead on the left full turns left, and
    ;; otherwise straight on. Turning right first keeps two regions that touch
    ;; only at a corner apart, as the 4-connected fill did.
    ;; The start corner is always a turn — the walk arrives at it heading
    ;; north up the start pixel's left edge — and is passed only once, because
    ;; the three pixels round it other than the start are all outside.
    (loop [x x0 y y0 d 0 out [x0 y0]]
      (let [[l r] (case d
                    0 [[x (dec y)] [x y]]
                    1 [[x y] [(dec x) y]]
                    2 [[(dec x) y] [(dec x) (dec y)]]
                    3 [[(dec x) (dec y)] [x (dec y)]])
            nd (cond (not (apply in? r)) (mod (inc d) 4)
                     (apply in? l)       (mod (+ d 3) 4)
                     :else               d)
            out (if (or (= nd d) (and (= x x0) (= y y0))) out (conj out x y))
            [dx dy] (dirs nd)
            nx (+ x dx) ny (+ y dy)]
        (if (and (= nx x0) (= ny y0))
          out
          (recur nx ny nd out))))))

(defn- bounds
  "`[x0 y0 x1 y1]` round what the mask holds, a pixel wider each way so a gap
  open to the outside reaches the edge of it — or nil for an empty mask."
  [{:keys [w h ^js buf]}]
  (let [b #js [w h -1 -1]]
    (dotimes [o (* w h)]
      (when (== 1 (aget buf o))
        (let [x (mod o w) y (quot o w)]
          (aset b 0 (min (aget b 0) x)) (aset b 1 (min (aget b 1) y))
          (aset b 2 (max (aget b 2) x)) (aset b 3 (max (aget b 3) y)))))
    (when (<= 0 (aget b 2))
      [(max 0 (dec (aget b 0))) (max 0 (dec (aget b 1)))
       (min (dec w) (inc (aget b 2))) (min (dec h) (inc (aget b 3)))])))

(defn- buffer-pieces
  "Each piece of the mask bigger than `smallest` pixels, biggest first, as
  `{:outer ring :holes [ring …]}` at full resolution: a hole is a gap inside a
  piece that does not reach the outside, of more than `smallest` pixels."
  ([m] (buffer-pieces m 2))
  ([{:keys [w h ^js buf] :as m} smallest]
   (when-let [[x0 y0 x1 y1 :as box] (bounds m)]
     (let [lab   (js/Int32Array. (* w h))
           stack (js/Int32Array. (* w h))
           ;; Pieces get positive labels and gaps negative ones, in raster
           ;; order, so each is labelled from its own first pixel.
           found (let [acc (array)]
                   (doseq [y (range y0 (inc y1))]
                     (dotimes [i (inc (- x1 x0))]
                       (let [o (+ x0 i (* y w))]
                         (when (zero? (aget lab o))
                           (let [ink (aget buf o)
                                 id  (cond-> (inc (.-length acc)) (zero? ink) -)]
                             (.push acc (assoc (flood! lab buf w box ink o id stack)
                                               :id id :start o)))))))
                   (vec acc))
           in    (fn [id] (fn [x y] (and (< -1 x w) (< -1 y h) (== id (aget lab (+ x (* y w)))))))
           ;; A hole's first pixel has a pixel of its piece directly above it:
           ;; anything else above would be the hole itself, and earlier.
           holes (group-by #(aget lab (- (:start %) w))
                           (filter #(and (neg? (:id %)) (not (:edge? %)) (< smallest (:size %))) found))]
       (->> found
            (filter #(and (pos? (:id %)) (< smallest (:size %))))
            (sort-by (comp - :size))
            (mapv (fn [{:keys [id start]}]
                    {:outer (ring w (in id) start)
                     :holes (mapv #(ring w (in (:id %)) (:start %)) (get holes id))})))))))

(defn pieces
  ([m] (pieces m 2))
  ([m smallest]
   (let [[ox oy] (or (:origin m) [0 0])
         shift (fn [ring] (mapv (fn [i v] (+ v (if (even? i) ox oy))) (range) ring))]
     (mapv (fn [p] (-> p (update :outer shift) (update :holes #(mapv shift %))))
           (buffer-pieces m smallest)))))

(defn rings
  "The outside of every piece of the mask, as flat corner points, biggest
  first."
  ([m] (rings m 2))
  ([m smallest] (mapv :outer (pieces m smallest))))

(defn- area
  "The area inside ring `pts`, by the shoelace."
  [pts]
  (let [ps (vec (partition 2 pts)) n (count ps)]
    (js/Math.abs (/ (reduce + (map (fn [i] (let [[ax ay] (ps i) [bx by] (ps (mod (inc i) n))]
                                             (- (* ax by) (* bx ay))))
                                   (range n)))
                    2))))

(defn- bridge
  "Ring `outer` with `hole` joined into it: from the hole's leftmost point,
  along its row to the nearest edge of `outer` on the left, round the hole, and
  back. Both are on a whole-pixel row, so the bridge is never filled. See the
  namespace docstring."
  [outer hole]
  (let [hs (vec (partition 2 hole))
        k  (apply min-key (comp first hs) (range (count hs)))
        [hx hy] (hs k)
        os (vec (partition 2 outer))
        n  (count os)
        ;; The edge the row meets nearest on the left. A level edge is skipped:
        ;; running along one draws nothing either.
        [i qx] (->> (range n)
                    (keep (fn [i]
                            (let [[ax ay] (os i) [bx by] (os (mod (inc i) n))]
                              (when (and (not= ay by) (<= (min ay by) hy (max ay by)))
                                (let [x (+ ax (* (/ (- hy ay) (- by ay)) (- bx ax)))]
                                  (when (< x hx) [i x]))))))
                    (reduce (fn [best c] (if (or (nil? best) (< (second best) (second c))) c best))
                            nil))]
    (if i
      (vec (concat (apply concat (subvec os 0 (inc i)))
                   [qx hy]
                   (apply concat (subvec hs k)) (apply concat (subvec hs 0 k))
                   [hx hy qx hy]
                   (apply concat (subvec os (inc i)))))
      outer)))

(defn- dp
  "Douglas-Peucker over the open run of points `i`..`j` of `xs`/`ys`: the
  indices kept so that no point between is further than `tol` from the line."
  [^js xs ^js ys i j tol]
  (let [ax (aget xs i) ay (aget ys i) bx (aget xs j) by (aget ys j)
        dx (- bx ax) dy (- by ay) l (js/Math.hypot dx dy)
        [k d] (reduce (fn [[_ best :as acc] k]
                        (let [px (- (aget xs k) ax) py (- (aget ys k) ay)
                              e  (if (zero? l) (js/Math.hypot px py) (/ (js/Math.abs (- (* dx py) (* dy px))) l))]
                          (if (< best e) [k e] acc)))
                      [nil 0] (range (inc i) j))]
    (if (and k (< tol d))
      (into (dp xs ys i k tol) (rest (dp xs ys k j tol)))
      [i j])))

(defn fit
  "Ring `pts` with as few points as keep every point of it within `tol` pixels
  of the result: Douglas-Peucker, closed by splitting at the point furthest from
  the first. A pixel staircase within a pixel of a diagonal becomes the
  diagonal, and a square corner stays — the points go where the shape bends."
  [pts tol]
  (let [c (quot (count pts) 2)]
    (if (<= c 3)
      (vec pts)
      (let [xs (js/Float64Array. (take-nth 2 pts)) ys (js/Float64Array. (take-nth 2 (rest pts)))
            far (apply max-key #(js/Math.hypot (- (aget xs %) (aget xs 0)) (- (aget ys %) (aget ys 0)))
                       (range c))
            xs2 (js/Float64Array. (inc c)) ys2 (js/Float64Array. (inc c))
            _ (dotimes [k c] (aset xs2 k (aget xs k)) (aset ys2 k (aget ys k)))
            _ (do (aset xs2 c (aget xs 0)) (aset ys2 c (aget ys 0)))
            keep (into (dp xs2 ys2 0 far tol) (rest (butlast (dp xs2 ys2 far c tol))))]
        (if (< (count keep) 3)
          (vec pts)
          (into [] (mapcat (fn [k] [(aget xs k) (aget ys k)])) keep))))))

(defn- crosses?
  "Does any edge of ring `a` cross any edge of ring `b`?"
  [a b]
  (let [edges (fn [r] (let [ps (vec (partition 2 r)) n (count ps)]
                        (map (fn [i] [(ps i) (ps (mod (inc i) n))]) (range n))))
        side  (fn [[ax ay] [bx by] [px py]] (- (* (- bx ax) (- py ay)) (* (- by ay) (- px ax))))]
    (some (fn [[p q]]
            (some (fn [[r s]]
                    (and (neg? (* (side p q r) (side p q s)))
                         (neg? (* (side r s p) (side r s q)))))
                  (edges b)))
          (edges a))))

(defn- perimeter [pts]
  (let [ps (vec (partition 2 pts)) n (count ps)]
    (reduce + (map (fn [i] (let [[ax ay] (ps i) [bx by] (ps (mod (inc i) n))]
                             (js/Math.hypot (- bx ax) (- by ay))))
                   (range n)))))

(defn ->js
  "Flat ring `ring` as `polygon-clipping` takes one."
  [ring]
  (into-array (map into-array (partition 2 ring))))

(defn ->ring
  "A ring back from `polygon-clipping`, flat, without the point it repeats to
  close."
  [^js r]
  (into [] (mapcat identity) (butlast (map vec (array-seq r)))))

(defn rings-of
  "Piece `p` of `pieces` fitted to within `tol` pixels, outside first, holes
  after.

  EVERY HOLE IS KEPT, and kept simple: fitted like the outside, then clipped to
  the inside of it and clear of the holes before it. Fitting each ring on its
  own can push a hole's edge across the outside's, and a crossing is what makes
  the even-odd fill cut through the body; clipping takes exactly that part off
  and leaves the few points the fit chose. Fitting closer until nothing crossed
  was tried, and spent hundreds of points on what is plainly a straight line."
  [{:keys [outer holes]} tol]
  (let [outer (fit outer tol)]
    (reduce (fn [rs hole]
              (let [h (fit hole tol)]
                (if (not-any? #(crosses? h %) rs)
                  (conj rs h)
                  (let [inside (clipping/intersection #js [(->js h)] #js [(->js outer)])
                        clear  (if (next rs)
                                 (clipping/difference inside (into-array (map #(array (->js %)) (rest rs))))
                                 inside)]
                    (into rs (comp (map #(->ring (aget % 0))) (filter #(< 2 (area %))))
                          (array-seq clear))))))
            [outer]
            (sort-by (comp - area) holes))))

(defn join
  "Rings `[outer & holes]` as one ring, each hole bridged in, leftmost first."
  [[outer & holes]]
  (reduce bridge outer (sort-by #(apply min (take-nth 2 %)) holes)))

(defn polygon
  "Piece `p` of `pieces` as the one ring a shape is made of. See `rings-of`."
  [p tol]
  (join (rings-of p tol)))
